/**
 * View state behind CompanionAppBanner.vue (docs/features/companion-app-banner/prd.md).
 * Kept out of the .vue file so every behaviour is unit-tested: the mocha shim
 * stubs `.vue` imports to nothing. Rotation and storage rules live in
 * `companionAppBannerRules.ts`.
 */
import { COMPANION_APPS, listingHref, CompanionApp, CompanionAppsSurface } from '@/config/companionApps';
import {
  trackCompanionAppClicked,
  trackCompanionAppDismissed,
  trackCompanionAppsViewed,
} from '@/modules/companionAppEvents';
import {
  isBannerSuppressedPath,
  localDay,
  pickBannerApp,
  readBannerState,
  recordPick,
  retireApp,
} from './companionAppBannerRules';

const SURFACE: CompanionAppsSurface = 'shell_banner';
const CATALOG_IDS = COMPANION_APPS.map((a) => a.id);
/** Once per browser session, so the view count is comparable with clicks. */
const VIEWED_SESSION_KEY = 'companion_app_banner_viewed';

export class CompanionBannerModel {
  private storeId: number | null = null;

  private app: CompanionApp | null = null;

  private acted = false;

  /**
   * Read the state once per store: the first render may have no store yet
   * (RF 1), and a store switch in the same tab loads that store's own state.
   */
  public load(storeId: number | null | undefined, now: Date = new Date()): void {
    if (typeof storeId !== 'number' || storeId === this.storeId) {
      return;
    }
    this.storeId = storeId;
    this.acted = false;
    this.app = null;
    const stored = readBannerState(storeId);
    if (!stored) {
      // Storage blocked: a ✕ that cannot stick would return on every page (B8).
      return;
    }
    const { appId, state } = pickBannerApp(CATALOG_IDS, stored, localDay(now));
    if (state !== stored && state.last) {
      recordPick(storeId, state.last);
    }
    this.app = COMPANION_APPS.find((a) => a.id === appId) || null;
  }

  public visibleApp(path: string): CompanionApp | null {
    if (this.storeId === null || this.acted || isBannerSuppressedPath(path)) {
      return null;
    }
    return this.app;
  }

  public markViewed(): void {
    try {
      if (sessionStorage.getItem(VIEWED_SESSION_KEY)) {
        return;
      }
      sessionStorage.setItem(VIEWED_SESSION_KEY, '1');
    } catch {
      // No session storage: count this view rather than none.
    }
    trackCompanionAppsViewed(SURFACE);
  }

  /** "Set up now" and ✕ both retire the app, and the banner hides for the day (B4, B5). */
  public retire(kind: 'click' | 'dismiss', now: Date = new Date()): void {
    if (!this.app || this.storeId === null || this.acted) {
      return;
    }
    if (kind === 'click') {
      trackCompanionAppClicked(SURFACE, this.app.id);
    } else {
      trackCompanionAppDismissed(SURFACE, this.app.id);
    }
    retireApp(this.storeId, this.app.id, localDay(now));
    this.acted = true;
  }

  public href(app: CompanionApp): string {
    return listingHref(app, SURFACE);
  }
}
